import type { StageId } from '@/layout/types';

/**
 * One glyph per editable stage, drawn on a 16×16 grid as open strokes so it
 * inherits `currentColor` and works on both a dark card and a white one.
 *
 * The same four appear on the drawer tabs and on every node they govern, so a
 * card on the canvas points at the fields that change it:
 *
 *   population  people      — the member base, built from subpopulations
 *   reach       speech      — a live conversation actually happened
 *   screen      clipboard   — a PHQ instrument was completed
 *   chase       clock       — the 30-day window that defines the follow-up
 */
export function StageIconPaths({ stage }: { stage: StageId }) {
  switch (stage) {
    case 'source':
      return (
        <>
          <circle cx="6" cy="5" r="2.6" />
          <path d="M1.6 13.6a4.4 4.4 0 0 1 8.8 0" />
          <path d="M11 3.1a2.6 2.6 0 0 1 0 4.6" />
          <path d="M12.4 9.9a4.4 4.4 0 0 1 2 3.7" />
        </>
      );
    case 'reach':
      return (
        <path d="M2.2 2.4h11.6a1.7 1.7 0 0 1 1.7 1.7v5.8a1.7 1.7 0 0 1-1.7 1.7H6.6l-3.4 2.8v-2.8H2.2a1.7 1.7 0 0 1-1.7-1.7V4.1a1.7 1.7 0 0 1 1.7-1.7Z" />
      );
    case 'screen':
      return (
        <>
          <path d="M10 2.7h1.9a1.5 1.5 0 0 1 1.5 1.5v9.3a1.5 1.5 0 0 1-1.5 1.5H4.1a1.5 1.5 0 0 1-1.5-1.5V4.2a1.5 1.5 0 0 1 1.5-1.5H6" />
          <rect x="5.9" y="1.1" width="4.2" height="2.9" rx="0.9" />
          <path d="m5.7 9.5 1.7 1.7 3.2-3.5" />
        </>
      );
    case 'chase':
      return (
        <>
          <circle cx="8" cy="8" r="6.4" />
          <path d="M8 4.3V8l2.6 1.7" />
        </>
      );
  }
}

/** HTML context: drawer tabs and the drawer header. */
export function StageIcon({ stage, size = 13 }: { stage: StageId; size?: number }) {
  return (
    <svg className="stage-icon" viewBox="0 0 16 16" width={size} height={size} aria-hidden="true">
      <StageIconPaths stage={stage} />
    </svg>
  );
}

/**
 * A pencil, drawn on the same 16×16 open-stroke grid as the stage glyphs so it
 * sits next to them consistently. Marks the entry point into the stage editor
 * rather than any one stage, so it lives outside the `StageId` switch above.
 */
export function EditIcon({ size = 14 }: { size?: number }) {
  return (
    <svg className="stage-icon" viewBox="0 0 16 16" width={size} height={size} aria-hidden="true">
      <path d="M10.7 2.8 13.2 5.3 5.7 12.8 2.8 13.2 3.2 10.3Z" />
      <path d="M9.3 4.2 11.8 6.7" />
    </svg>
  );
}
